import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import cls from "./UserMemberPartnerCreate.module.scss";
import api from "@/utils/axios";
import { toast } from "react-toastify";

import {
  RELATIONSHIP_TYPES,
  RELATIONSHIP_STATUSES,
} from "@/enums/PartnerEnums";

import {
  GENDERS,
  EDUCATION_LEVELS,
  LOYALTY_LEVELS,
  FINANCIAL_LEVELS,
  PRIORITY_LEVELS,
  WORK_WITH_US_ROLES,
  COUNTRIES,
} from "@/enums/MemberEnums";

import {
  SEXUAL_IDENTITIES,
  SEXUAL_ROLES,
  ATTRACTION_GENDERS,
  CONNECTION_TYPES,
  INTIMACY_TYPES,
  SEXUAL_FANTASIES,
  BODY_TYPES,
  BODY_ATTRACT,
  SEXUAL_ENVIRONMENTS,
  EVENT_ATTRACT,
  ALCOHOL_CANNABIS,
  BE_MATCHED,
  JOIN_HOST,
} from "@/enums/lifestyleEnums";

const UserMemberPartnerCreate = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);

  // Relationship
  const [partnerUserId, setPartnerUserId] = useState("");
  const [relationshipType, setRelationshipType] = useState("");
  const [relationshipStatus, setRelationshipStatus] = useState("");

  // Personal
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [birthday, setBirthday] = useState("");
  const [gender, setGender] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");
  const [mobilePhone, setMobilePhone] = useState("");
  const [workPlaceName, setWorkPlaceName] = useState("");
  const [workPosition, setWorkPosition] = useState("");
  const [responsibleNotes, setResponsibleNotes] = useState("");
  const [educationLevel, setEducationLevel] = useState("");
  const [schoolNote, setSchoolNote] = useState("");
  const [universityNote, setUniversityNote] = useState("");
  const [socialFacebook, setSocialFacebook] = useState("");
  const [socialInstagram, setSocialInstagram] = useState("");
  const [socialTikTok, setSocialTikTok] = useState("");
  const [socialYouTube, setSocialYouTube] = useState("");
  const [loyalty, setLoyalty] = useState("");
  const [financial, setFinancial] = useState("");
  const [priority, setPriority] = useState("");
  const [workWithUs, setWorkWithUs] = useState("");

  // Lifestyle (single-choice)
  const [sexualIdentity, setSexualIdentity] = useState("");
  const [sexualRole, setSexualRole] = useState("");
  const [bodyType, setBodyType] = useState("");
  const [alcoholCannabis, setAlcoholCannabis] = useState("");
  const [joinHost, setJoinHost] = useState("");

  // Lifestyle (multi-choice)
  const [attractionGenders, setAttractionGenders] = useState([]);
  const [connectionTypes, setConnectionTypes] = useState([]);
  const [intimacyTypes, setIntimacyTypes] = useState([]);
  const [sexualFantasies, setSexualFantasies] = useState([]);
  const [bodyAttract, setBodyAttract] = useState([]);
  const [sexualEnvironment, setSexualEnvironment] = useState([]);
  const [eventAttract, setEventAttract] = useState([]);
  const [beMatched, setBeMatched] = useState([]);

  const formatLabel = (v) => {
    if (!v) return "";
    return v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const toggleInArray = (value, current, setter) => {
    if (current.includes(value)) {
      setter(current.filter((v) => v !== value));
    } else {
      setter([...current, value]);
    }
  };

  const handleBack = () => {
    navigate(`/admin/members/single/partners-info/${userId}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!relationshipType) {
      toast.error("Please select a relationship type");
      return;
    }
    if (!relationshipStatus) {
      toast.error("Please select a relationship status");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        partnerUserId: partnerUserId || undefined,
        relationshipType,
        relationshipStatus,
        personal: {
          firstName: firstName || "",
          lastName: lastName || "",
          birthday: birthday || undefined,
          gender: gender || undefined,
          country: country || undefined,
          city: city || "",
          address: address || "",
          mobilePhone: mobilePhone || "",
          workPlaceName: workPlaceName || "",
          workPosition: workPosition || "",
          responsibleNotes: responsibleNotes || "",
          educationLevel: educationLevel || undefined,
          schoolNote: schoolNote || "",
          universityNote: universityNote || "",
          socialFacebook: socialFacebook || "",
          socialInstagram: socialInstagram || "",
          socialTikTok: socialTikTok || "",
          socialYouTube: socialYouTube || "",
          loyalty: loyalty || undefined,
          financial: financial || undefined,
          priority: priority || undefined,
          work_with_us: workWithUs || undefined,
        },
        lifestyle: {
          sexual_identity: sexualIdentity || undefined,
          sexual_role: sexualRole || undefined,
          body_type: bodyType || undefined,
          alcohol_cannabis: alcoholCannabis || undefined,
          join_host: joinHost || undefined,
          attraction_genders: attractionGenders,
          connection_types: connectionTypes,
          intimacy_types: intimacyTypes,
          sexual_fantasies: sexualFantasies,
          body_attract: bodyAttract,
          sexual_environment: sexualEnvironment,
          event_attract: eventAttract,
          be_matched: beMatched,
        },
      };

      await api.post(
        `/v1/admin/members/single/partners-info/create/${userId}`,
        payload
      );

      toast.success("Partner created successfully");
      navigate(`/admin/members/single/partners-info/${userId}`);
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.join(", ") ||
        "Failed to create partner";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={cls.page}>
      {/* Header */}
      <div className={cls.header}>
        <button type="button" className={cls.backBtn} onClick={handleBack}>
          ← Back to partners
        </button>
        <h1 className={cls.title}>Create New Partner</h1>
        <p className={cls.subtitle}>
          Add a new partner to this member. If this is their first partner, they
          will be upgraded to a couple and this partner becomes primary.
        </p>
      </div>

      {/* Layout */}
      <div className={cls.layout}>
        {/* Left: Relationship overview card */}
        <section className={cls.summaryCard}>
          <div className={cls.cardHeader}>
            <h2 className={cls.cardTitle}>Relationship Snapshot</h2>
            <p className={cls.cardSubtitle}>
              A quick glance at how this partner connects to the member.
            </p>
          </div>

          <div className={cls.summaryBody}>
            <div className={cls.summaryRow}>
              <span className={cls.summaryLabel}>Linked User ID</span>
              <span className={cls.summaryValue}>
                {partnerUserId ? partnerUserId : "Standalone partner"}
              </span>
            </div>
            <div className={cls.summaryRow}>
              <span className={cls.summaryLabel}>Relationship Type</span>
              <span className={cls.summaryValue}>
                {relationshipType ? formatLabel(relationshipType) : "Not set"}
              </span>
            </div>
            <div className={cls.summaryRow}>
              <span className={cls.summaryLabel}>Relationship Status</span>
              <span className={cls.summaryValue}>
                {relationshipStatus
                  ? formatLabel(relationshipStatus)
                  : "Not set"}
              </span>
            </div>

            <div className={cls.summaryHintBox}>
              <p className={cls.summaryHintTitle}>Primary partner logic</p>
              <p className={cls.summaryHintText}>
                If this member is currently <strong>single</strong>, the system
                will automatically:
              </p>
              <ul className={cls.summaryList}>
                <li>change their member type to couple</li>
                <li>mark this partner as their primary partner</li>
              </ul>
              <p className={cls.summaryHintTextSmall}>
                If they already have partners, this one will be added as a{" "}
                <strong>non-primary</strong> partner.
              </p>
            </div>
          </div>
        </section>

        {/* Right: Main form */}
        <section className={cls.formCard}>
          <form className={cls.form} onSubmit={handleSubmit}>
            {/* Relationship section */}
            <div className={cls.section}>
              <h2 className={cls.sectionTitle}>Relationship Details</h2>
              <p className={cls.sectionSubtitle}>
                Define how this partner is connected to the member in the club.
              </p>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="partnerUserId">
                    Linked User ID (optional)
                  </label>
                  <input
                    id="partnerUserId"
                    className={cls.input}
                    value={partnerUserId}
                    onChange={(e) => setPartnerUserId(e.target.value)}
                    placeholder="If this partner has their own user account"
                  />
                </div>
              </div>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label}>Relationship Type</label>
                  <div className={cls.chipGroupWrap}>
                    {RELATIONSHIP_TYPES.map((t) => (
                      <button
                        type="button"
                        key={t}
                        className={
                          relationshipType === t
                            ? `${cls.chip} ${cls.chipActive}`
                            : cls.chip
                        }
                        onClick={() =>
                          setRelationshipType(relationshipType === t ? "" : t)
                        }
                      >
                        {formatLabel(t)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={cls.field}>
                  <label className={cls.label}>Relationship Status</label>
                  <div className={cls.chipGroupWrap}>
                    {RELATIONSHIP_STATUSES.map((s) => (
                      <button
                        type="button"
                        key={s}
                        className={
                          relationshipStatus === s
                            ? `${cls.chip} ${cls.chipActive}`
                            : cls.chip
                        }
                        onClick={() =>
                          setRelationshipStatus(
                            relationshipStatus === s ? "" : s
                          )
                        }
                      >
                        {formatLabel(s)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Personal section */}
            <div className={cls.section}>
              <h2 className={cls.sectionTitle}>Private Identity</h2>
              <p className={cls.sectionSubtitle}>
                Real-world details, only visible to admins and trust team.
              </p>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="firstName">
                    First name
                  </label>
                  <input
                    id="firstName"
                    className={cls.input}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Real first name"
                  />
                </div>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="lastName">
                    Last name
                  </label>
                  <input
                    id="lastName"
                    className={cls.input}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Real last name"
                  />
                </div>
              </div>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="birthday">
                    Birthday
                  </label>
                  <input
                    id="birthday"
                    type="date"
                    className={cls.input}
                    value={birthday}
                    onChange={(e) => setBirthday(e.target.value)}
                  />
                </div>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="gender">
                    Gender
                  </label>
                  <select
                    id="gender"
                    className={cls.select}
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                  >
                    <option value="">Select gender</option>
                    {GENDERS.map((g) => (
                      <option key={g} value={g}>
                        {formatLabel(g)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="country">
                    Country
                  </label>
                  <select
                    id="country"
                    className={cls.select}
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                  >
                    <option value="">Select country</option>
                    {COUNTRIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="city">
                    City
                  </label>
                  <input
                    id="city"
                    className={cls.input}
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="City"
                  />
                </div>
              </div>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="address">
                    Address (private)
                  </label>
                  <input
                    id="address"
                    className={cls.input}
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Street / area"
                  />
                </div>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="mobilePhone">
                    Mobile phone
                  </label>
                  <input
                    id="mobilePhone"
                    className={cls.input}
                    value={mobilePhone}
                    onChange={(e) => setMobilePhone(e.target.value)}
                    placeholder="+1 403 ..."
                  />
                </div>
              </div>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="workPlaceName">
                    Workplace / Company
                  </label>
                  <input
                    id="workPlaceName"
                    className={cls.input}
                    value={workPlaceName}
                    onChange={(e) => setWorkPlaceName(e.target.value)}
                    placeholder="Company name"
                  />
                </div>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="workPosition">
                    Position / Role
                  </label>
                  <input
                    id="workPosition"
                    className={cls.input}
                    value={workPosition}
                    onChange={(e) => setWorkPosition(e.target.value)}
                    placeholder="Job title"
                  />
                </div>
              </div>

              <div className={cls.field}>
                <label className={cls.label} htmlFor="responsibleNotes">
                  Responsibility & trust notes
                </label>
                <textarea
                  id="responsibleNotes"
                  className={cls.textarea}
                  rows={3}
                  value={responsibleNotes}
                  onChange={(e) => setResponsibleNotes(e.target.value)}
                  placeholder="Anything that helps us trust this partner more."
                />
              </div>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="educationLevel">
                    Education level
                  </label>
                  <select
                    id="educationLevel"
                    className={cls.select}
                    value={educationLevel}
                    onChange={(e) => setEducationLevel(e.target.value)}
                  >
                    <option value="">Select level</option>
                    {EDUCATION_LEVELS.map((lvl) => (
                      <option key={lvl} value={lvl}>
                        {formatLabel(lvl)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="loyalty">
                    Loyalty level
                  </label>
                  <select
                    id="loyalty"
                    className={cls.select}
                    value={loyalty}
                    onChange={(e) => setLoyalty(e.target.value)}
                  >
                    <option value="">Select</option>
                    {LOYALTY_LEVELS.map((lvl) => (
                      <option key={lvl} value={lvl}>
                        {formatLabel(lvl)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="financial">
                    Financial level
                  </label>
                  <select
                    id="financial"
                    className={cls.select}
                    value={financial}
                    onChange={(e) => setFinancial(e.target.value)}
                  >
                    <option value="">Select</option>
                    {FINANCIAL_LEVELS.map((lvl) => (
                      <option key={lvl} value={lvl}>
                        {formatLabel(lvl)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="priority">
                    Club priority
                  </label>
                  <select
                    id="priority"
                    className={cls.select}
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                  >
                    <option value="">Select</option>
                    {PRIORITY_LEVELS.map((lvl) => (
                      <option key={lvl} value={lvl}>
                        {formatLabel(lvl)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="schoolNote">
                    School / College notes
                  </label>
                  <textarea
                    id="schoolNote"
                    className={cls.textarea}
                    rows={2}
                    value={schoolNote}
                    onChange={(e) => setSchoolNote(e.target.value)}
                    placeholder="Any relevant school/college details."
                  />
                </div>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="universityNote">
                    University notes
                  </label>
                  <textarea
                    id="universityNote"
                    className={cls.textarea}
                    rows={2}
                    value={universityNote}
                    onChange={(e) => setUniversityNote(e.target.value)}
                    placeholder="Degrees, masters, certifications."
                  />
                </div>
              </div>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="socialFacebook">
                    Facebook
                  </label>
                  <input
                    id="socialFacebook"
                    className={cls.input}
                    value={socialFacebook}
                    onChange={(e) => setSocialFacebook(e.target.value)}
                    placeholder="Profile URL / @handle"
                  />
                </div>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="socialInstagram">
                    Instagram
                  </label>
                  <input
                    id="socialInstagram"
                    className={cls.input}
                    value={socialInstagram}
                    onChange={(e) => setSocialInstagram(e.target.value)}
                    placeholder="@username"
                  />
                </div>
              </div>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="socialTikTok">
                    TikTok
                  </label>
                  <input
                    id="socialTikTok"
                    className={cls.input}
                    value={socialTikTok}
                    onChange={(e) => setSocialTikTok(e.target.value)}
                    placeholder="@username"
                  />
                </div>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="socialYouTube">
                    YouTube
                  </label>
                  <input
                    id="socialYouTube"
                    className={cls.input}
                    value={socialYouTube}
                    onChange={(e) => setSocialYouTube(e.target.value)}
                    placeholder="Channel URL"
                  />
                </div>
              </div>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label} htmlFor="workWithUs">
                    Work with us (role)
                  </label>
                  <select
                    id="workWithUs"
                    className={cls.select}
                    value={workWithUs}
                    onChange={(e) => setWorkWithUs(e.target.value)}
                  >
                    <option value="">None</option>
                    {WORK_WITH_US_ROLES.map((w) => (
                      <option key={w} value={w}>
                        {formatLabel(w)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Lifestyle section */}
            <div className={cls.section}>
              <h2 className={cls.sectionTitle}>Lifestyle Map</h2>
              <p className={cls.sectionSubtitle}>
                Attractions, fantasies and environments to match this partner
                with the right vibes.
              </p>

              {/* Identity & role */}
              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label}>Sexual identity</label>
                  <div className={cls.chipGroup}>
                    {SEXUAL_IDENTITIES.map((id) => (
                      <button
                        type="button"
                        key={id}
                        className={
                          sexualIdentity === id
                            ? `${cls.chip} ${cls.chipActive}`
                            : cls.chip
                        }
                        onClick={() =>
                          setSexualIdentity(sexualIdentity === id ? "" : id)
                        }
                      >
                        {formatLabel(id)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={cls.field}>
                  <label className={cls.label}>Sexual role</label>
                  <div className={cls.chipGroup}>
                    {SEXUAL_ROLES.map((r) => (
                      <button
                        type="button"
                        key={r}
                        className={
                          sexualRole === r
                            ? `${cls.chip} ${cls.chipActive}`
                            : cls.chip
                        }
                        onClick={() => setSexualRole(sexualRole === r ? "" : r)}
                      >
                        {formatLabel(r)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Attraction & connections */}
              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label}>Attraction genders</label>
                  <div className={cls.chipGroupWrap}>
                    {ATTRACTION_GENDERS.map((g) => (
                      <label key={g} className={cls.checkChip}>
                        <input
                          type="checkbox"
                          checked={attractionGenders.includes(g)}
                          onChange={() =>
                            toggleInArray(
                              g,
                              attractionGenders,
                              setAttractionGenders
                            )
                          }
                        />
                        <span>{formatLabel(g)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className={cls.field}>
                  <label className={cls.label}>Connection types</label>
                  <div className={cls.chipGroupWrap}>
                    {CONNECTION_TYPES.map((c) => (
                      <label key={c} className={cls.checkChip}>
                        <input
                          type="checkbox"
                          checked={connectionTypes.includes(c)}
                          onChange={() =>
                            toggleInArray(
                              c,
                              connectionTypes,
                              setConnectionTypes
                            )
                          }
                        />
                        <span>{formatLabel(c)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Intimacy & fantasies */}
              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label}>Intimacy types</label>
                  <div className={cls.chipGroupWrap}>
                    {INTIMACY_TYPES.map((v) => (
                      <label key={v} className={cls.checkChip}>
                        <input
                          type="checkbox"
                          checked={intimacyTypes.includes(v)}
                          onChange={() =>
                            toggleInArray(v, intimacyTypes, setIntimacyTypes)
                          }
                        />
                        <span>{formatLabel(v)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className={cls.field}>
                  <label className={cls.label}>Sexual fantasies</label>
                  <div className={cls.chipGroupWrap}>
                    {SEXUAL_FANTASIES.map((v) => (
                      <label key={v} className={cls.checkChip}>
                        <input
                          type="checkbox"
                          checked={sexualFantasies.includes(v)}
                          onChange={() =>
                            toggleInArray(
                              v,
                              sexualFantasies,
                              setSexualFantasies
                            )
                          }
                        />
                        <span>{formatLabel(v)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Body & environment */}
              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label}>Body type</label>
                  <div className={cls.chipGroup}>
                    {BODY_TYPES.map((b) => (
                      <button
                        type="button"
                        key={b}
                        className={
                          bodyType === b
                            ? `${cls.chip} ${cls.chipActive}`
                            : cls.chip
                        }
                        onClick={() => setBodyType(bodyType === b ? "" : b)}
                      >
                        {formatLabel(b)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={cls.field}>
                  <label className={cls.label}>Body attractions</label>
                  <div className={cls.chipGroupWrap}>
                    {BODY_ATTRACT.map((v) => (
                      <label key={v} className={cls.checkChip}>
                        <input
                          type="checkbox"
                          checked={bodyAttract.includes(v)}
                          onChange={() =>
                            toggleInArray(v, bodyAttract, setBodyAttract)
                          }
                        />
                        <span>{formatLabel(v)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label}>Preferred environments</label>
                  <div className={cls.chipGroupWrap}>
                    {SEXUAL_ENVIRONMENTS.map((v) => (
                      <label key={v} className={cls.checkChip}>
                        <input
                          type="checkbox"
                          checked={sexualEnvironment.includes(v)}
                          onChange={() =>
                            toggleInArray(
                              v,
                              sexualEnvironment,
                              setSexualEnvironment
                            )
                          }
                        />
                        <span>{formatLabel(v)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className={cls.field}>
                  <label className={cls.label}>Event attraction</label>
                  <div className={cls.chipGroupWrap}>
                    {EVENT_ATTRACT.map((v) => (
                      <label key={v} className={cls.checkChip}>
                        <input
                          type="checkbox"
                          checked={eventAttract.includes(v)}
                          onChange={() =>
                            toggleInArray(v, eventAttract, setEventAttract)
                          }
                        />
                        <span>{formatLabel(v)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Matching & substances */}
              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label}>Alcohol & cannabis</label>
                  <div className={cls.chipGroup}>
                    {ALCOHOL_CANNABIS.map((v) => (
                      <button
                        type="button"
                        key={v}
                        className={
                          alcoholCannabis === v
                            ? `${cls.chip} ${cls.chipActive}`
                            : cls.chip
                        }
                        onClick={() =>
                          setAlcoholCannabis(alcoholCannabis === v ? "" : v)
                        }
                      >
                        {formatLabel(v)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={cls.field}>
                  <label className={cls.label}>Be matched preferences</label>
                  <div className={cls.chipGroupWrap}>
                    {BE_MATCHED.map((v) => (
                      <label key={v} className={cls.checkChip}>
                        <input
                          type="checkbox"
                          checked={beMatched.includes(v)}
                          onChange={() =>
                            toggleInArray(v, beMatched, setBeMatched)
                          }
                        />
                        <span>{formatLabel(v)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className={cls.formRow}>
                <div className={cls.field}>
                  <label className={cls.label}>Join / host style</label>
                  <div className={cls.chipGroup}>
                    {JOIN_HOST.map((v) => (
                      <button
                        type="button"
                        key={v}
                        className={
                          joinHost === v
                            ? `${cls.chip} ${cls.chipActive}`
                            : cls.chip
                        }
                        onClick={() => setJoinHost(joinHost === v ? "" : v)}
                      >
                        {formatLabel(v)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className={cls.actions}>
              <button
                type="button"
                className={cls.secondaryBtn}
                onClick={handleBack}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={cls.primaryBtn}
                disabled={saving}
              >
                {saving ? "Creating…" : "Create Partner"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default UserMemberPartnerCreate;
